/**
 * Canonical permission keys + defaults for the PRM SPA (slices 5 + 6).
 *
 * Mirrors `apps/api/src/prm_api/auth/permissions.py`. The two files
 * must stay in lock-step — `tests/unit/test_permissions.py` on the
 * backend and `tests/lib/permissions.test.ts` here both pin the
 * canonical keys, labels, and per-role defaults.
 *
 * 10 per-user permission flags surface in the InviteUserDialog step-2
 * "Configure Permissions" screen for CM invites (Flow C, slice 5) and
 * RA invites (Flow D, slice 6 — admin with `is_resetdata_master=false`).
 * RM bypasses every check via `is_resetdata_master=true` and never sees
 * the step-2 UI. Stored in `users.permissions` JSONB as
 * `{Perm_Key: bool}`; missing keys default to `false` via
 * `effectivePermissions()`.
 */

// ---------------------------------------------------------------------------
// Canonical permission keys (verbatim from Confluence pageId 701268014
// Flow C step-2 ASCII mockup; spec anchor 2026-05-22, version 86).
// ---------------------------------------------------------------------------

export type PermKey =
  | "Perm_Invite_CM"
  | "Perm_Amend_CP_Perms"
  | "Perm_Deal_Adjudicate"
  | "Perm_Ext_Adjudicate"
  | "Perm_ClosedWon_Adjudicate"
  | "Perm_SPIF_Adjudicate"
  | "Perm_Asset_Upload"
  | "Perm_Asset_Amend"
  | "Perm_Training_Upload"
  | "Perm_Training_Edit";

export const PERM_KEYS: readonly PermKey[] = [
  "Perm_Invite_CM",
  "Perm_Amend_CP_Perms",
  "Perm_Deal_Adjudicate",
  "Perm_Ext_Adjudicate",
  // CHP-376: closed-won adjudication sits immediately after deal-extension
  // adjudication in the Deal & SPIF Management section.
  "Perm_ClosedWon_Adjudicate",
  "Perm_SPIF_Adjudicate",
  "Perm_Asset_Upload",
  "Perm_Asset_Amend",
  "Perm_Training_Upload",
  "Perm_Training_Edit",
] as const;

// Human-readable labels (verbatim from the Confluence "Permissions
// framework" table).
export const PERM_LABEL: Record<PermKey, string> = {
  Perm_Invite_CM: "Invite Channel Managers",
  Perm_Amend_CP_Perms: "Amend permissions of channel partners",
  Perm_Deal_Adjudicate: "Approve/Reject Deal Registrations",
  Perm_Ext_Adjudicate: "Approve/Reject Deal Extensions",
  Perm_ClosedWon_Adjudicate: "Approve/Reject Closed-won deal",
  Perm_SPIF_Adjudicate: "Approve/Reject SPIF payments",
  Perm_Asset_Upload: "Upload new assets",
  Perm_Asset_Amend: "Amend existing assets",
  Perm_Training_Upload: "Upload new training",
  Perm_Training_Edit: "Edit training",
};

// UI grouping (per Flow C ASCII mockup — 3 sections). The Confluence
// table uses 4 sections but the mockup collapses Deal+SPIF and
// Assets+Training. The mockup section labels are what the
// InviteUserDialog step 2 renders.
export type SectionKey = "User Management" | "Deal & SPIF Management" | "Content & Enablement";

export const PERM_SECTIONS: Record<SectionKey, readonly PermKey[]> = {
  "User Management": ["Perm_Invite_CM", "Perm_Amend_CP_Perms"],
  "Deal & SPIF Management": [
    "Perm_Deal_Adjudicate",
    "Perm_Ext_Adjudicate",
    "Perm_ClosedWon_Adjudicate",
    "Perm_SPIF_Adjudicate",
  ],
  "Content & Enablement": [
    "Perm_Asset_Upload",
    "Perm_Asset_Amend",
    "Perm_Training_Upload",
    "Perm_Training_Edit",
  ],
};

// Sections render top-to-bottom in this order in step 2 of the
// InviteUserDialog. Pulled out so the component iterates in spec
// order without trusting Object.keys insertion semantics.
export const SECTION_ORDER: readonly SectionKey[] = [
  "User Management",
  "Deal & SPIF Management",
  "Content & Enablement",
] as const;

// Default-state-by-role for new invites. CM (slice 5) + RA (slice 6).
// RM is bypassed via is_resetdata_master so it has no stored defaults.
//
// Source of truth is the Confluence "Permissions framework" TABLE,
// not the ASCII mockup — they disagree on 5 of 9 CM cells, and the
// spec narrative ("Toggles default to standard Channel Manager
// baselines") plus Christopher Sassone's note on the table make the
// table authoritative. Mockup-vs-table reconciliation was flagged in
// the slice 5 PR body for designer follow-up.
//
// RA defaults are byte-for-byte identical to CM defaults today per
// the same table. They're spelled out independently (not aliased to
// `cm`) so the two columns can diverge in a future spec rev without
// an opaque indirection bug.
export const DEFAULTS_BY_ROLE: { cm: Record<PermKey, boolean>; ra: Record<PermKey, boolean> } = {
  cm: {
    Perm_Invite_CM: true,
    Perm_Amend_CP_Perms: false,
    Perm_Deal_Adjudicate: true,
    Perm_Ext_Adjudicate: true,
    // CHP-376: closed-won adjudication defaults ON for CM + RA (RM is
    // unconstrained and ignores the stored map via the bypass).
    Perm_ClosedWon_Adjudicate: true,
    Perm_SPIF_Adjudicate: false,
    Perm_Asset_Upload: false,
    Perm_Asset_Amend: false,
    Perm_Training_Upload: false,
    Perm_Training_Edit: false,
  },
  ra: {
    Perm_Invite_CM: true,
    Perm_Amend_CP_Perms: false,
    Perm_Deal_Adjudicate: true,
    Perm_Ext_Adjudicate: true,
    Perm_ClosedWon_Adjudicate: true,
    Perm_SPIF_Adjudicate: false,
    Perm_Asset_Upload: false,
    Perm_Asset_Amend: false,
    Perm_Training_Upload: false,
    Perm_Training_Edit: false,
  },
};

/**
 * Resolve a user's effective permission map.
 *
 * Rules:
 *   - `isResetdataMaster=true` → every key true regardless of stored.
 *     RM's stored perms aren't authoritative; the bypass takes
 *     precedence so a misconfigured RM row can't lock itself out.
 *   - Otherwise → stored value if present, else false.
 *
 * Contract: the returned object has every PERM_KEYS entry. Callers
 * can index by key without a `?? false` fallback.
 *
 * No callers yet — slice 5 lands the helper. Feature-route gating
 * wires in as /deals, /assets, /training endpoints ship.
 */
export function effectivePermissions(input: {
  stored: Record<string, boolean> | null | undefined;
  isResetdataMaster: boolean | null | undefined;
}): Record<PermKey, boolean> {
  const { stored, isResetdataMaster } = input;
  if (isResetdataMaster) {
    return Object.fromEntries(PERM_KEYS.map((k) => [k, true])) as Record<PermKey, boolean>;
  }
  const src = stored ?? {};
  return Object.fromEntries(PERM_KEYS.map((k) => [k, Boolean(src[k])])) as Record<PermKey, boolean>;
}

/**
 * Merge caller-supplied perms over the role's defaults.
 *
 * Used by the InviteUserDialog step 2: the dialog hands the user the
 * default-state toggles for the chosen role (CM via Flow C, RA via
 * Flow D); on submit we send the merged dict so the backend persists
 * a full snapshot at invite time.
 *
 * Unknown keys are dropped silently — the backend schema validator
 * already 422s on unknowns, so by the time we call this, `explicit`
 * has been validated client-side via the PERM_KEYS-restricted form.
 */
export function mergeWithRoleDefaults(
  role: "cm" | "ra",
  explicit: Partial<Record<PermKey, boolean>> | null | undefined,
): Record<PermKey, boolean> {
  const base: Record<PermKey, boolean> = { ...DEFAULTS_BY_ROLE[role] };
  if (explicit) {
    for (const k of PERM_KEYS) {
      if (k in explicit && typeof explicit[k] === "boolean") {
        base[k] = explicit[k] as boolean;
      }
    }
  }
  return base;
}
